import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Search, Loader2, GraduationCap, Download, Upload, Plus, Eye, Pencil,
  ChevronLeft, ChevronRight, CheckCircle2, XCircle, AlertTriangle,
  User, Mail, Phone, BookOpen, Briefcase, ArrowLeft, FileSpreadsheet,
  X, ChevronDown, Filter, Send, RefreshCw, MailCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  nameError, emailError, mobileError, textError, selectError, divisionError, admissionError,
  apiFieldError,
} from '@/lib/formValidation';
import { EMAIL_PROPS, TEL_PROPS } from '@/lib/mobileForm';

const FieldHint = ({ error }) => (
  error ? <p className="text-[11px] text-red-600 mt-1" data-testid="field-error">{error}</p> : null
);

const FIELD_LABELS = {
  first_name: 'First name', last_name: 'Last name', grade: 'Grade', division: 'Division',
  admission_number: 'Admission number', parent_name: 'Parent name', relationship: 'Relationship',
  parent_email: 'Parent email', parent_mobile: 'Parent mobile',
  parent_profession: 'Parent profession', parent2_name: 'Second parent name',
  parent2_relationship: 'Second parent relationship', parent2_email: 'Second parent email',
  parent2_mobile: 'Second parent mobile', parent2_profession: 'Second parent profession',
  interests: 'Interests',
};

const fieldClass = (error) => `mt-1 h-11 text-base sm:h-9 sm:text-sm ${error ? 'border-red-400' : ''}`;

const ONBOARDING_LABELS = {
  not_invited: 'Not Invited',
  invited: 'Invited',
  awaiting_parent_activation: 'Awaiting Activation',
  awaiting_consent: 'Awaiting Consent',
  active: 'Active',
  inactive: 'Inactive',
};

const ONBOARDING_COLORS = {
  not_invited: 'bg-gray-100 text-gray-600',
  invited: 'bg-blue-50 text-blue-700',
  awaiting_parent_activation: 'bg-amber-50 text-amber-700',
  awaiting_consent: 'bg-violet-50 text-violet-600',
  active: 'bg-emerald-50 text-emerald-600',
  inactive: 'bg-red-50 text-red-600',
};

const GRADES = ['9', '10', '11', '12'];
const PAGE_SIZE = 15;

// ─── Student Profile View ───
function StudentProfile({ studentId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/coordinator/students/${studentId}/profile`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load profile'))
      .finally(() => setLoading(false));
  }, [studentId]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#9CA3AF]" /></div>;
  if (!data) return <div className="text-center py-12 text-sm text-[#9CA3AF]">Student not found.</div>;

  const s = data.student;
  const ob = s.onboarding_status || 'not_invited';

  return (
    <div data-testid="student-profile">
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#1F2937] mb-5 transition-colors" data-testid="profile-back-btn">
        <ArrowLeft className="h-4 w-4" /> Back to Students
      </button>

      <div className="bg-white rounded-xl border border-[#E5E7EB] p-5 mb-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#F97D6A] to-[#F9A88C] flex items-center justify-center text-white font-bold text-lg">
            {s.full_name?.[0] || '?'}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-[#1F2937] font-['Outfit']" data-testid="profile-student-name">{s.full_name}</h2>
            <p className="text-sm text-[#6B7280] mt-0.5">Grade {s.grade}{s.division ? ` · Division ${s.division}` : ''} · {s.admission_number}</p>
            <Badge className={`border-0 text-[10px] mt-1.5 ${ONBOARDING_COLORS[ob]}`}>{ONBOARDING_LABELS[ob]}</Badge>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Attendance record */}
        <AttendanceCard attendance={data.attendance} experiences={data.experiences} />

        {/* Student Details */}
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-5">
          <h3 className="text-sm font-bold text-[#1F2937] mb-3 flex items-center gap-2"><GraduationCap className="h-4 w-4 text-[#F97D6A]" /> Student Details</h3>
          <div className="space-y-2.5 text-sm">
            <Row label="First Name" value={s.first_name} />
            <Row label="Last Name" value={s.last_name} />
            <Row label="Grade" value={s.grade} />
            <Row label="Division" value={s.division || '—'} />
            <Row label="Admission No." value={s.admission_number} />
            <Row label="Interests" value={s.interests?.length ? s.interests.join(', ') : '—'} />
          </div>
        </div>

        {/* Parent & Guardian */}
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-5">
          <h3 className="text-sm font-bold text-[#1F2937] mb-3 flex items-center gap-2"><User className="h-4 w-4 text-[#F97D6A]" /> Parent & Guardian</h3>
          <div className="space-y-2.5 text-sm">
            <Row label="Name" value={s.parent_name || '—'} />
            <Row label="Relationship" value={s.relationship || '—'} />
            <Row label="Email" value={s.parent_email || '—'} />
            <Row label="Mobile" value={s.parent_mobile || '—'} />
            <Row label="Profession" value={s.parent_profession || <span className="text-amber-600">Not recorded</span>} />
            {s.parent2_name ? (
              <>
                <hr className="border-[#F3F4F6]" />
                <Row label="Second Parent" value={`${s.parent2_name}${s.parent2_relationship ? ` · ${s.parent2_relationship}` : ''}`} />
                <Row label="Email" value={s.parent2_email || '—'} />
                <Row label="Mobile" value={s.parent2_mobile || '—'} />
                <Row label="Profession" value={s.parent2_profession || <span className="text-amber-600">Not recorded</span>} />
                <hr className="border-[#F3F4F6]" />
              </>
            ) : null}
            <Row label="Account Status" value={data.parent_account ? (
              <Badge className={`border-0 text-[10px] ${data.parent_account.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>
                {data.parent_account.status || 'No account'}
              </Badge>
            ) : <span className="text-[#9CA3AF]">No account linked</span>} />
          </div>
        </div>

        {/* Onboarding & Consent */}
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-5">
          <h3 className="text-sm font-bold text-[#1F2937] mb-3 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#F97D6A]" /> Onboarding & Consent</h3>
          <div className="space-y-2.5 text-sm">
            <Row label="Invitation Status" value={ONBOARDING_LABELS[ob]} />
            <Row label="Account Activation" value={data.parent_account?.status === 'active' ? 'Activated' : 'Not activated'} />
            <Row label="Privacy Consent" value={
              data.consent?.has_consent ? (
                <span className="text-emerald-600 font-medium">
                  Granted{data.consent.consent_date ? ` · ${new Date(data.consent.consent_date).toLocaleDateString()}` : ''}
                </span>
              ) : data.consent?.withdrawn ? (
                <span className="text-red-600 font-medium">Withdrawn by parent</span>
              ) : (
                <span className="text-amber-600">Pending</span>
              )
            } />
            {data.consent?.has_consent && data.consent.policy_version && (
              <Row label="Policy Version" value={`v${data.consent.policy_version}`} />
            )}
          </div>
        </div>

        {/* Career Journey */}
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-5">
          <h3 className="text-sm font-bold text-[#1F2937] mb-3 flex items-center gap-2"><Briefcase className="h-4 w-4 text-[#F97D6A]" /> Career Journey</h3>
          {data.saved_careers?.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-medium text-[#6B7280] mb-1.5">Saved Careers</p>
              <div className="flex flex-wrap gap-1.5">
                {data.saved_careers.map(c => (
                  <Badge key={c.id} className="border-0 bg-blue-50 text-blue-700 text-[10px]">{c.name}</Badge>
                ))}
              </div>
            </div>
          )}
          {data.experiences?.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-medium text-[#6B7280] mb-1.5">Experiences ({data.experiences.length})</p>
              {data.experiences.map(e => (
                <div key={e.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-[#F3F4F6] last:border-0" data-testid={`profile-experience-${e.id}`}>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-[#1F2937]">{e.career_name || 'Experience'}</p>
                    <p className="text-[10px] text-[#9CA3AF]">{e.mentor_name ? `with ${e.mentor_name}` : ''}{e.assigned_date ? ` · ${e.assigned_date}` : ''}</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {ATTENDANCE_MARK[attendanceMark(e)] && (
                      <Badge className={`border-0 text-[9px] ${ATTENDANCE_MARK[attendanceMark(e)].tone}`} data-testid={`attendance-mark-${e.id}`}>
                        {ATTENDANCE_MARK[attendanceMark(e)].label}
                      </Badge>
                    )}
                    <Badge className={`border-0 text-[10px] ${e.status === 'completed' ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>{String(e.status || '').replace(/_/g, ' ')}</Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-[#9CA3AF]">
              {data.saved_careers?.length ? '' : 'No career journey data yet.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

const ATTENDANCE_MARK = {
  attended: { label: 'Attended', tone: 'bg-emerald-50 text-emerald-600' },
  missed: { label: 'Absent', tone: 'bg-red-50 text-red-600' },
  not_conducted: { label: 'Did not run', tone: 'bg-slate-100 text-slate-600' },
};

function attendanceMark(e) {
  if (['completed', 'awaiting_feedback'].includes(e.status)) return e.attended === false ? 'missed' : 'attended';
  if (e.status === 'not_conducted') return 'not_conducted';
  return null;
}

function AttendanceCard({ attendance, experiences = [] }) {
  const a = attendance || {};
  const rate = a.attendance_rate;
  const recent = experiences.filter(e => attendanceMark(e)).slice(0, 5);
  const recentMissed = recent.filter(e => attendanceMark(e) === 'missed').length;
  const tone = rate === null || rate === undefined ? 'text-[#9CA3AF]'
    : rate >= 80 ? 'text-emerald-600' : rate >= 50 ? 'text-amber-600' : 'text-red-600';

  const chips = [
    { key: 'attended', label: 'Attended', value: a.attended || 0, tone: 'bg-emerald-50 text-emerald-600' },
    { key: 'missed', label: 'Absent', value: a.missed || 0, tone: 'bg-red-50 text-red-600' },
    { key: 'not_conducted', label: 'Did not run', value: a.not_conducted || 0, tone: 'bg-[#F3F4F6] text-[#6B7280]' },
    { key: 'cancelled', label: 'Cancelled', value: a.cancelled || 0, tone: 'bg-[#F3F4F6] text-[#6B7280]' },
    { key: 'awaiting_closure', label: 'Awaiting closure', value: a.awaiting_closure || 0, tone: 'bg-[#F3F4F6] text-[#6B7280]' },
    { key: 'upcoming', label: 'Upcoming', value: a.upcoming || 0, tone: 'bg-[#F3F4F6] text-[#6B7280]' },
  ];

  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] p-5 lg:col-span-2" data-testid="attendance-card">
      <h3 className="text-sm font-bold text-[#1F2937] mb-1 flex items-center gap-2">
        <CheckCircle2 className="h-4 w-4 text-[#F97D6A]" /> Attendance Record
      </h3>
      <p className="text-[11px] text-[#9CA3AF] mb-3 max-w-xl leading-relaxed" data-testid="attendance-rule">{ATTENDANCE_RULE}</p>
      {!a.held ? (
        <div data-testid="attendance-empty">
          <p className="text-xs text-[#9CA3AF]">No experiences have been closed as completed yet, so there is no rate to report.</p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {chips.filter(c => c.value > 0).map(c => (
              <Badge key={c.key} className={`border-0 text-[10px] ${c.tone}`} data-testid={`attendance-chip-${c.key}`}>
                {c.value} {c.label}
              </Badge>
            ))}
          </div>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-6">
            <div>
              <p className={`text-3xl font-extrabold font-['Outfit'] ${tone}`} data-testid="attendance-rate">{rate}%</p>
              <p className="text-[11px] text-[#9CA3AF]">
                attended {a.attended} of {a.held} experience{a.held === 1 ? '' : 's'} closed as completed
              </p>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {chips.filter(c => c.value > 0).map(c => (
                <Badge key={c.key} className={`border-0 text-[10px] ${c.tone}`} data-testid={`attendance-chip-${c.key}`}>
                  {c.value} {c.label}
                </Badge>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-1.5 mt-4 max-w-sm" data-testid="attendance-timeline">
            {recent.slice().reverse().map(e => {
              const mark = attendanceMark(e);
              return (
                <span key={e.id} title={`${e.career_name || 'Experience'}${e.assigned_date ? ` · ${e.assigned_date}` : ''} — ${ATTENDANCE_MARK[mark].label}`}
                  className={`h-6 flex-1 rounded-md ${mark === 'attended' ? 'bg-emerald-400' : mark === 'missed' ? 'bg-red-400' : 'bg-slate-300'}`} />
              );
            })}
            <span className="text-[10px] text-[#9CA3AF] ml-1 shrink-0">last {recent.length}</span>
          </div>
          {recentMissed >= 2 && (
            <p className="text-[11px] text-red-600 bg-red-50 rounded-lg px-3 py-2 mt-3" data-testid="attendance-warning">
              Absent from {recentMissed} of the last {recent.length} experiences — worth a word with the family.
            </p>
          )}
          {a.last_attended_date && (
            <p className="text-[11px] text-[#9CA3AF] mt-2">Last attended on {new Date(a.last_attended_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
          )}
        </>
      )}
    </div>
  );
}

const ATTENDANCE_RULE = 'Counts only experiences closed as completed. Cancelled and not-conducted experiences are listed separately.';

function AttendanceCell({ record }) {
  const r = record || {};
  const notes = [
    r.cancelled ? `${r.cancelled} cancelled` : null,
    r.not_conducted ? `${r.not_conducted} did not run` : null,
    r.awaiting_closure ? `${r.awaiting_closure} to close off` : null,
  ].filter(Boolean).join(' · ');

  if (!r.held) {
    return (
      <div data-testid="attendance-cell-empty">
        <span className="text-[11px] text-[#C8CDD6]">{r.upcoming ? `${r.upcoming} upcoming` : 'None held'}</span>
        {notes && <p className="text-[10px] text-[#9CA3AF] mt-0.5 whitespace-nowrap">{notes}</p>}
      </div>
    );
  }
  const rate = r.attendance_rate;
  const tone = rate >= 80 ? 'text-emerald-600' : rate >= 50 ? 'text-amber-600' : 'text-red-600';
  return (
    <div title={`${r.attended} attended, ${r.missed} absent of ${r.held} held. ${ATTENDANCE_RULE}`} data-testid="attendance-cell">
      <div className="flex items-center gap-2">
        <span className={`text-xs font-bold ${tone}`}>{rate}%</span>
        <span className="h-1.5 w-12 rounded-full bg-[#F3F4F6] overflow-hidden shrink-0">
          <span className={`block h-full rounded-full ${rate >= 80 ? 'bg-emerald-500' : rate >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
            style={{ width: `${rate}%` }} />
        </span>
        <span className="text-[10px] text-[#9CA3AF] whitespace-nowrap">{r.attended}/{r.held}</span>
      </div>
      {notes && <p className="text-[10px] text-[#9CA3AF] mt-0.5 whitespace-nowrap" data-testid="attendance-cell-notes">{notes}</p>}
    </div>
  );
}

function Row({ label, value }) {  return (
    <div className="flex justify-between items-start gap-4">
      <span className="text-[#6B7280] shrink-0 min-w-[100px]">{label}</span>
      <span className="text-[#1F2937] font-medium text-right">{value || '—'}</span>
    </div>
  );
}

// ─── Add/Edit Student Dialog ───
function StudentFormDialog({ open, onOpenChange, student, onSaved }) {
  const isEdit = !!student;
  const [form, setForm] = useState({
    first_name: '', last_name: '', grade: '', admission_number: '',
    parent_name: '', relationship: 'Father', parent_email: '', parent_mobile: '', parent_profession: '',
    parent2_name: '', parent2_relationship: 'Mother', parent2_email: '', parent2_mobile: '', parent2_profession: '',
    division: '', interests: '',
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (k, v) => {
    setForm(p => ({ ...p, [k]: v }));
    setErrors(p => ({ ...p, [k]: '' }));
  };

  const validate = () => {
    const next = {
      first_name: nameError(form.first_name, 'First name'),
      last_name: nameError(form.last_name, 'Last name'),
      grade: selectError(form.grade, 'Grade'),
      division: divisionError(form.division),
      admission_number: admissionError(form.admission_number),
      parent_name: nameError(form.parent_name, 'Parent name'),
      relationship: selectError(form.relationship, 'Relationship'),
      parent_email: emailError(form.parent_email, 'Parent email'),
      parent_mobile: mobileError(form.parent_mobile, 'Parent mobile'),
      parent_profession: textError(form.parent_profession, 'Parent profession'),
      parent2_name: nameError(form.parent2_name, 'Second parent name', { required: false }),
      parent2_email: emailError(form.parent2_email, 'Second parent email', { required: false }),
      parent2_mobile: mobileError(form.parent2_mobile, 'Second parent mobile', { required: false }),
      parent2_profession: form.parent2_name.trim()
        ? textError(form.parent2_profession, 'Second parent profession')
        : '',
      interests: textError(form.interests, 'Interests', { required: false, max: 300, min: 1 }),
    };
    if (!next.parent2_email && form.parent2_name.trim() && form.parent2_email.trim()
        && form.parent2_email.trim().toLowerCase() === form.parent_email.trim().toLowerCase()) {
      next.parent2_email = 'The second parent needs a different email from the first parent';
    }
    setErrors(next);
    return Object.values(next).every(v => !v);
  };

  useEffect(() => {
    if (open && student) {
      setForm({
        first_name: student.first_name || student.full_name?.split(' ')[0] || '',
        last_name: student.last_name || student.full_name?.split(' ').slice(1).join(' ') || '',
        grade: student.grade || '', admission_number: student.admission_number || '',
        parent_name: student.parent_name || '', relationship: student.relationship || 'Father',
        parent_email: student.parent_email || '', parent_mobile: student.parent_mobile || '',
        parent_profession: student.parent_profession || '',
        parent2_name: student.parent2_name || '', parent2_relationship: student.parent2_relationship || 'Mother',
        parent2_email: student.parent2_email || '', parent2_mobile: student.parent2_mobile || '',
        parent2_profession: student.parent2_profession || '',
        division: student.division || '', interests: student.interests?.join(', ') || '',
      });
    } else if (open) {
      setForm({ first_name: '', last_name: '', grade: '', admission_number: '', parent_name: '', relationship: 'Father', parent_email: '', parent_mobile: '', parent_profession: '', parent2_name: '', parent2_relationship: 'Mother', parent2_email: '', parent2_mobile: '', parent2_profession: '', division: '', interests: '' });
    }
    if (open) setErrors({});
  }, [open, student]);

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/coordinator/students/${student.id || student._id}`, form);
        toast.success('Student updated');
      } else {
        await api.post('/coordinator/students', form);
        toast.success('Student added');
      }
      onSaved();
      onOpenChange(false);
    } catch (e) {
      const { field, message } = apiFieldError(e, 'Failed to save');
      if (field) setErrors(p => ({ ...p, [field]: message }));
      else toast.error(message);
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="student-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit']">{isEdit ? 'Edit Student' : 'Add Student'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm text-[#6B7280]">First Name *</Label>
              <Input value={form.first_name} onChange={e => set('first_name', e.target.value)} placeholder="First name" className={fieldClass(errors.first_name)} data-testid="form-first-name" />
              <FieldHint error={errors.first_name} />
            </div>
            <div>
              <Label className="text-sm text-[#6B7280]">Last Name *</Label>
              <Input value={form.last_name} onChange={e => set('last_name', e.target.value)} placeholder="Last name" className={fieldClass(errors.last_name)} data-testid="form-last-name" />
              <FieldHint error={errors.last_name} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-sm text-[#6B7280]">Grade *</Label>
              <Select value={form.grade} onValueChange={v => set('grade', v)}>
                <SelectTrigger className={fieldClass(errors.grade)} data-testid="form-grade"><SelectValue placeholder="Grade" /></SelectTrigger>
                <SelectContent>{GRADES.map(g => <SelectItem key={g} value={g}>Grade {g}</SelectItem>)}</SelectContent>
              </Select>
              <FieldHint error={errors.grade} />
            </div>
            <div>
              <Label className="text-sm text-[#6B7280]">Division *</Label>
              <Input value={form.division} onChange={e => set('division', e.target.value)} placeholder="e.g. A" className={fieldClass(errors.division)} data-testid="form-division" />
              <FieldHint error={errors.division} />
            </div>
            <div>
              <Label className="text-sm text-[#6B7280]">Admission No. *</Label>
              <Input value={form.admission_number} onChange={e => set('admission_number', e.target.value)} placeholder="ADM-001" className={fieldClass(errors.admission_number)} data-testid="form-admission" />
              <FieldHint error={errors.admission_number} />
            </div>
          </div>
          <hr className="border-[#E5E7EB]" />
          <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Parent / Guardian</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm text-[#6B7280]">Parent Name *</Label>
              <Input value={form.parent_name} onChange={e => set('parent_name', e.target.value)} placeholder="Full name" className={fieldClass(errors.parent_name)} data-testid="form-parent-name" />
              <FieldHint error={errors.parent_name} />
            </div>
            <div>
              <Label className="text-sm text-[#6B7280]">Relationship *</Label>
              <Select value={form.relationship} onValueChange={v => set('relationship', v)}>
                <SelectTrigger className="mt-1 h-11 text-base sm:h-9 sm:text-sm" data-testid="form-relationship"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Father', 'Mother', 'Guardian', 'Other'].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm text-[#6B7280]">Parent Email *</Label>
              <Input {...EMAIL_PROPS} value={form.parent_email} onChange={e => set('parent_email', e.target.value)} placeholder="parent@email.com" className={fieldClass(errors.parent_email)} data-testid="form-parent-email" />
              <FieldHint error={errors.parent_email} />
            </div>
            <div>
              <Label className="text-sm text-[#6B7280]">Parent Mobile *</Label>
              <Input {...TEL_PROPS} value={form.parent_mobile} onChange={e => set('parent_mobile', e.target.value)} placeholder="+919876543210" className={fieldClass(errors.parent_mobile)} data-testid="form-parent-mobile" />
              <FieldHint error={errors.parent_mobile} />
            </div>
          </div>
          <div>
            <Label className="text-sm text-[#6B7280]">Parent Profession *</Label>
            <Input value={form.parent_profession} onChange={e => set('parent_profession', e.target.value)} placeholder="e.g. Civil Engineer" className={fieldClass(errors.parent_profession)} data-testid="form-parent-profession" />
            <FieldHint error={errors.parent_profession} />
            <p className="text-[10px] text-[#9CA3AF] mt-1">Used to find parents who could talk about a career.</p>
          </div>
          <hr className="border-[#E5E7EB]" />
          <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Second Parent (optional)</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm text-[#6B7280]">Name</Label>
              <Input value={form.parent2_name} onChange={e => set('parent2_name', e.target.value)} placeholder="Full name" className={fieldClass(errors.parent2_name)} data-testid="form-parent2-name" />
              <FieldHint error={errors.parent2_name} />
            </div>
            <div>
              <Label className="text-sm text-[#6B7280]">Relationship</Label>
              <Select value={form.parent2_relationship} onValueChange={v => set('parent2_relationship', v)}>
                <SelectTrigger className="mt-1 h-11 text-base sm:h-9 sm:text-sm" data-testid="form-parent2-relationship"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Father', 'Mother', 'Guardian', 'Other'].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm text-[#6B7280]">Email</Label>
              <Input {...EMAIL_PROPS} value={form.parent2_email} onChange={e => set('parent2_email', e.target.value)} placeholder="parent@email.com" className={fieldClass(errors.parent2_email)} data-testid="form-parent2-email" />
              <FieldHint error={errors.parent2_email} />
            </div>
            <div>
              <Label className="text-sm text-[#6B7280]">Mobile</Label>
              <Input {...TEL_PROPS} value={form.parent2_mobile} onChange={e => set('parent2_mobile', e.target.value)} placeholder="+919876543210" className={fieldClass(errors.parent2_mobile)} data-testid="form-parent2-mobile" />
              <FieldHint error={errors.parent2_mobile} />
            </div>
          </div>
          <div>
            <Label className="text-sm text-[#6B7280]">Profession {form.parent2_name.trim() ? '*' : ''}</Label>
            <Input value={form.parent2_profession} onChange={e => set('parent2_profession', e.target.value)} placeholder="e.g. Architect" className={fieldClass(errors.parent2_profession)} data-testid="form-parent2-profession" />
            <FieldHint error={errors.parent2_profession} />
          </div>
          <hr className="border-[#E5E7EB]" />
          <div>
            <Label className="text-sm text-[#6B7280]">Interests (optional, comma-separated)</Label>
            <Input value={form.interests} onChange={e => set('interests', e.target.value)} placeholder="Technology, Medicine" className={fieldClass(errors.interests)} data-testid="form-interests" />
            <FieldHint error={errors.interests} />
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg text-sm h-9">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="rounded-lg text-sm h-11 sm:h-9 w-full sm:w-auto bg-[#F97D6A] hover:bg-[#e86c5a] text-white" data-testid="form-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            {isEdit ? 'Update' : 'Add Student'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Upload Dialog ───
function UploadDialog({ open, onOpenChange, onImported }) {
  const [step, setStep] = useState('upload'); // upload | preview | importing | done
  const [file, setFile] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) { setStep('upload'); setFile(null); setValidationResult(null); setImportResult(null); }
  }, [open]);

  const handleFileSelect = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.endsWith('.xlsx')) { toast.error('Only .xlsx files supported'); return; }
    setFile(f);
    setStep('preview');

    const formData = new FormData();
    formData.append('file', f);
    try {
      const r = await api.post('/coordinator/students/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setValidationResult(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
      setStep('upload');
    }
  };

  const handleImport = async () => {
    if (!validationResult || !validationResult.can_import) return;

    setImporting(true);
    setStep('importing');
    try {
      const r = await api.post('/coordinator/students/import', { rows: validationResult.rows });
      setImportResult(r.data);
      setStep('done');
      toast.success(`${r.data.imported} students imported`);
    } catch (err) {
      const { message, rows } = apiFieldError(err, 'Import failed');
      toast.error(message);
      if (rows) {
        // The server re-checked every row and refused the file: show its reasons
        setValidationResult(prev => prev && {
          ...prev,
          can_import: false,
          rows: prev.rows.map(row => {
            const problem = rows.find(p => p.row === row.row);
            return problem ? { ...row, status: 'error', errors: problem.errors } : row;
          }),
        });
      }
      setStep('preview');
    }
    setImporting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit']">
            {step === 'upload' && 'Upload Students'}
            {step === 'preview' && 'Review Upload'}
            {step === 'importing' && 'Importing...'}
            {step === 'done' && 'Import Complete'}
          </DialogTitle>
        </DialogHeader>

        {step === 'upload' && (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <FileSpreadsheet className="h-12 w-12 text-[#F97D6A] mb-4" />
            <p className="text-sm text-[#1F2937] font-medium mb-1">Upload your student spreadsheet</p>
            <p className="text-xs text-[#9CA3AF] mb-5 max-w-sm">
              Use the CareersPOV template (.xlsx). Each row represents one student with parent details.
            </p>
            <input type="file" ref={fileRef} accept=".xlsx" onChange={handleFileSelect} className="hidden" data-testid="upload-file-input" />
            <Button onClick={() => fileRef.current?.click()} className="rounded-lg bg-[#F97D6A] hover:bg-[#e86c5a] text-white h-9 text-sm px-6" data-testid="upload-select-file-btn">
              <Upload className="h-4 w-4 mr-2" /> Select File
            </Button>
          </div>
        )}

        {step === 'preview' && validationResult && (
          <div className="flex-1 overflow-hidden flex flex-col">
            <div className="flex items-center gap-3 mb-3 flex-wrap" data-testid="upload-summary">
              <span className="text-sm font-medium text-[#1F2937]" data-testid="upload-total-count">{validationResult.total_rows} rows</span>
              <div className="flex items-center gap-1.5 text-sm">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                <span className="font-medium text-emerald-700" data-testid="upload-valid-count">{validationResult.valid_rows} valid</span>
              </div>
              <div className="flex items-center gap-1.5 text-sm">
                <XCircle className={`h-4 w-4 ${validationResult.invalid_rows ? 'text-red-500' : 'text-gray-300'}`} />
                <span className={`font-medium ${validationResult.invalid_rows ? 'text-red-600' : 'text-gray-400'}`} data-testid="upload-invalid-count">{validationResult.invalid_rows} invalid</span>
              </div>
              <div className="flex items-center gap-1.5 text-sm">
                <AlertTriangle className={`h-4 w-4 ${validationResult.duplicate_rows ? 'text-amber-500' : 'text-gray-300'}`} />
                <span className={`font-medium ${validationResult.duplicate_rows ? 'text-amber-600' : 'text-gray-400'}`} data-testid="upload-duplicate-count">{validationResult.duplicate_rows} duplicate</span>
              </div>
              <span className="text-xs text-[#9CA3AF]">{file?.name}</span>
            </div>
            {!validationResult.can_import && (
              <div className="mb-3 rounded-xl bg-red-50 border border-red-100 px-4 py-2.5 text-xs text-red-700" data-testid="upload-blocked-note">
                Nothing will be imported while any row has a problem. Fix the rows listed below in your
                spreadsheet and upload it again — no student is added or overwritten in the meantime.
              </div>
            )}

            <ScrollArea className="flex-1 max-h-[400px] border rounded-lg">
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="upload-preview-table">
                  <thead className="bg-[#F8F9FA] sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-[#6B7280]">#</th>
                      <th className="px-3 py-2 text-left font-semibold text-[#6B7280]">Name</th>
                      <th className="px-3 py-2 text-left font-semibold text-[#6B7280]">Grade</th>
                      <th className="px-3 py-2 text-left font-semibold text-[#6B7280]">Adm. No.</th>
                      <th className="px-3 py-2 text-left font-semibold text-[#6B7280]">Parent</th>
                      <th className="px-3 py-2 text-left font-semibold text-[#6B7280]">Email</th>
                      <th className="px-3 py-2 text-left font-semibold text-[#6B7280]">Profession</th>
                      <th className="px-3 py-2 text-left font-semibold text-[#6B7280]">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validationResult.rows.map((r, i) => (
                      <tr key={i} className={`border-t border-[#F3F4F6] ${r.status === 'error' ? 'bg-red-50/50' : r.status === 'duplicate' ? 'bg-amber-50/50' : ''}`} data-testid={`upload-row-${i}`}>
                        <td className="px-3 py-2 text-[#9CA3AF]">{r.row}</td>
                        <td className="px-3 py-2 text-[#1F2937] font-medium">{r.first_name} {r.last_name}</td>
                        <td className="px-3 py-2">{r.grade}</td>
                        <td className="px-3 py-2">{r.admission_number}</td>
                        <td className="px-3 py-2">{r.parent_name}</td>
                        <td className="px-3 py-2">{r.parent_email}</td>
                        <td className="px-3 py-2">{r.parent_profession || '—'}</td>
                        <td className="px-3 py-2">
                          {r.status === 'valid' ? (
                            <Badge className="border-0 bg-emerald-50 text-emerald-600 text-[10px]">Valid</Badge>
                          ) : (
                            <div>
                              <Badge className={`border-0 text-[10px] ${r.status === 'duplicate' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-600'}`}>
                                {r.status === 'duplicate' ? 'Duplicate' : 'Error'}
                              </Badge>
                              <ul className="mt-1 space-y-0.5" data-testid={`upload-row-errors-${r.row}`}>
                                {(r.errors || []).map((e, j) => (
                                  <li key={j} className={`text-[10px] ${r.status === 'duplicate' ? 'text-amber-700' : 'text-red-600'}`}>
                                    {typeof e === 'string'
                                      ? `Row ${r.row} — ${e}`
                                      : `Row ${r.row} · ${FIELD_LABELS[e.field] || e.field} — ${e.message}`}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ScrollArea>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => { setStep('upload'); setFile(null); setValidationResult(null); }} className="rounded-lg text-sm h-9">Back</Button>
              <Button onClick={handleImport} disabled={!validationResult.can_import} className="rounded-lg text-sm h-9 bg-[#F97D6A] hover:bg-[#e86c5a] text-white" data-testid="upload-import-btn">
                {validationResult.can_import
                  ? `Import ${validationResult.valid_rows} Student${validationResult.valid_rows !== 1 ? 's' : ''}`
                  : 'Import blocked — fix the rows above'}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 'preview' && !validationResult && (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#9CA3AF]" /></div>
        )}

        {step === 'importing' && (
          <div className="flex flex-col items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#F97D6A] mb-3" />
            <p className="text-sm text-[#6B7280]">Importing students...</p>
          </div>
        )}

        {step === 'done' && importResult && (
          <div className="text-center py-8">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
            <p className="text-lg font-bold text-[#1F2937] font-['Outfit'] mb-1">Import Complete</p>
            <p className="text-sm text-[#6B7280] mb-4">
              {importResult.imported} student{importResult.imported !== 1 ? 's' : ''} added
            </p>
            <Button onClick={() => { onOpenChange(false); onImported(); }} className="rounded-lg text-sm h-9 bg-[#F97D6A] hover:bg-[#e86c5a] text-white" data-testid="upload-done-btn">
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Send Invitations Dialog ───
function InvitationDialog({ open, onOpenChange, selectedIds, onSent }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open && selectedIds.length > 0) {
      setLoading(true);
      setResult(null);
      api.get(`/coordinator/invitation-stats?ids=${selectedIds.join(',')}`)
        .then(r => setStats(r.data))
        .catch(() => toast.error('Failed to load invitation stats'))
        .finally(() => setLoading(false));
    }
  }, [open, selectedIds]);

  const handleSend = async () => {
    setSending(true);
    try {
      const r = await api.post('/coordinator/students/send-invitations', { student_ids: selectedIds });
      setResult(r.data);
      toast.success(`${r.data.sent} invitation${r.data.sent !== 1 ? 's' : ''} sent`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send invitations');
    }
    setSending(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="invitation-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit']">
            {result ? 'Invitations Sent' : 'Send Parent Invitations'}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#9CA3AF]" /></div>
        )}

        {!loading && !result && stats && (
          <div className="space-y-4 mt-2">
            <div className="bg-[#F8F9FA] rounded-xl p-4 space-y-2.5">
              <div className="flex justify-between text-sm">
                <span className="text-[#6B7280]">Selected Students</span>
                <span className="font-semibold text-[#1F2937]" data-testid="inv-total-students">{stats.total_students}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#6B7280]">Unique Parents</span>
                <span className="font-semibold text-[#1F2937]" data-testid="inv-unique-parents">{stats.unique_parents}</span>
              </div>
              {stats.already_invited > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-[#6B7280]">Already Invited</span>
                  <span className="font-semibold text-amber-600" data-testid="inv-already-invited">{stats.already_invited}</span>
                </div>
              )}
              <hr className="border-[#E5E7EB]" />
              <div className="flex justify-between text-sm">
                <span className="text-[#1F2937] font-medium">Invitations to Send</span>
                <span className="font-bold text-[#F97D6A] text-base" data-testid="inv-to-send">{stats.to_send}</span>
              </div>
            </div>
            {stats.to_send === 0 && (
              <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">All selected parents have already been invited.</p>
            )}
          </div>
        )}

        {result && (
          <div className="text-center py-4">
            <MailCheck className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
            <p className="text-sm text-[#6B7280]">
              <span className="font-semibold text-emerald-600">{result.sent}</span> invitation{result.sent !== 1 ? 's' : ''} sent
              {result.already_invited > 0 && <span>, <span className="text-amber-600">{result.already_invited}</span> already invited</span>}
              {result.failed > 0 && <span>, <span className="text-red-600">{result.failed}</span> failed</span>}
              {result.skipped_no_email > 0 && <span>, <span className="text-gray-500">{result.skipped_no_email}</span> skipped (no email)</span>}
            </p>
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button onClick={() => { onOpenChange(false); onSent(); }} className="rounded-lg text-sm h-9 bg-[#F97D6A] hover:bg-[#e86c5a] text-white" data-testid="inv-done-btn">
              Done
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg text-sm h-9">Cancel</Button>
              <Button onClick={handleSend} disabled={sending || !stats || stats.to_send === 0} className="rounded-lg text-sm h-9 bg-[#F97D6A] hover:bg-[#e86c5a] text-white" data-testid="inv-confirm-btn">
                {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
                Confirm & Send
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Students Tab ───
export function StudentsTab() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [gradeFilter, setGradeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [divisionFilter, setDivisionFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [showAdd, setShowAdd] = useState(false);
  const [editStudent, setEditStudent] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [profileId, setProfileId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [showInvite, setShowInvite] = useState(false);
  const [resending, setResending] = useState(null);
  const [attendance, setAttendance] = useState({});

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const [r, att] = await Promise.all([
        api.get('/children'),
        api.get('/coordinator/students/attendance').catch(() => ({ data: {} })),
      ]);
      setAttendance(att.data || {});
      // Enrich with coordinator-specific fields
      setStudents(r.data.map(s => ({
        ...s,
        onboarding_status: s.onboarding_status || 'not_invited',
        parent_name: s.parent_name || '',
        parent_email: s.parent_email || '',
        parent_mobile: s.parent_mobile || '',
      })));
    } catch {
      toast.error('Failed to load students');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchStudents(); }, [fetchStudents]);

  // Unique divisions
  const divisions = [...new Set(students.map(s => s.division).filter(Boolean))].sort();

  // Filter
  const filtered = students.filter(s => {
    if (search) {
      const q = search.toLowerCase();
      const match = (s.full_name || '').toLowerCase().includes(q)
        || (s.admission_number || '').toLowerCase().includes(q)
        || (s.parent_name || '').toLowerCase().includes(q)
        || (s.parent_email || '').toLowerCase().includes(q);
      if (!match) return false;
    }
    if (gradeFilter !== 'all' && s.grade !== gradeFilter) return false;
    if (divisionFilter !== 'all' && s.division !== divisionFilter) return false;
    if (statusFilter !== 'all' && (s.onboarding_status || 'not_invited') !== statusFilter) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => { setPage(1); }, [search, gradeFilter, statusFilter, divisionFilter]);

  const handleDownloadTemplate = async () => {
    try {
      const r = await api.get('/coordinator/students/template', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'CareersPOV_Student_Upload_Template.xlsx'; a.click();
      URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch {
      toast.error('Failed to download template');
    }
  };

  const toggleSelectAll = () => {
    if (selected.size === paginated.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(paginated.map(s => s.id || s._id)));
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const handleResend = async (childId) => {
    setResending(childId);
    try {
      await api.post(`/coordinator/students/${childId}/resend-invitation`);
      toast.success('Invitation resent');
      fetchStudents();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resend');
    }
    setResending(null);
  };

  // Count eligible students for invitation
  const eligibleForInvite = [...selected].filter(id => {
    const s = students.find(st => (st.id || st._id) === id);
    return s && s.parent_email && s.parent_email.includes('@');
  });

  if (profileId) {
    return <StudentProfile studentId={profileId} onBack={() => { setProfileId(null); fetchStudents(); }} />;
  }

  return (
    <div data-testid="students-tab">
      {/* Top bar: actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9CA3AF]" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, admission no, parent..." className="pl-9 h-11 text-base sm:h-9 sm:text-sm rounded-lg border-[#E5E7EB]" data-testid="student-search" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={handleDownloadTemplate} className="rounded-lg text-xs h-11 sm:h-8 border-[#E5E7EB] text-[#4B5563]" data-testid="download-template-btn">
            <Download className="h-3.5 w-3.5 mr-1.5" /> Template
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowUpload(true)} className="rounded-lg text-xs h-11 sm:h-8 border-[#E5E7EB] text-[#4B5563]" data-testid="upload-students-btn">
            <Upload className="h-3.5 w-3.5 mr-1.5" /> Upload
          </Button>
          <Button size="sm" onClick={() => { setEditStudent(null); setShowAdd(true); }} className="rounded-lg text-xs h-11 sm:h-8 bg-[#F97D6A] hover:bg-[#e86c5a] text-white" data-testid="add-student-btn">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Student
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="flex items-center gap-1 text-xs text-[#9CA3AF]">
          <Filter className="h-3 w-3" /> Filters:
        </div>
        <Select value={gradeFilter} onValueChange={setGradeFilter}>
          <SelectTrigger className="h-11 sm:h-7 text-base sm:text-xs w-32 sm:w-24 rounded-md border-[#E5E7EB]" data-testid="filter-grade"><SelectValue placeholder="Grade" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Grades</SelectItem>
            {GRADES.map(g => <SelectItem key={g} value={g}>Grade {g}</SelectItem>)}
          </SelectContent>
        </Select>
        {divisions.length > 0 && (
          <Select value={divisionFilter} onValueChange={setDivisionFilter}>
            <SelectTrigger className="h-11 sm:h-7 text-base sm:text-xs w-32 sm:w-28 rounded-md border-[#E5E7EB]" data-testid="filter-division"><SelectValue placeholder="Division" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Divisions</SelectItem>
              {divisions.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-11 sm:h-7 text-base sm:text-xs w-36 sm:w-32 rounded-md border-[#E5E7EB]" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(ONBOARDING_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-[#9CA3AF] ml-auto">{filtered.length} student{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 mb-4 bg-blue-50 rounded-xl px-4 py-2.5 border border-blue-100" data-testid="bulk-action-bar">
          <span className="text-sm font-medium text-blue-800">{selected.size} selected</span>
          <Button size="sm" onClick={() => setShowInvite(true)} disabled={eligibleForInvite.length === 0}
            className="rounded-lg text-xs h-11 sm:h-7 bg-[#F97D6A] hover:bg-[#e86c5a] text-white" data-testid="bulk-send-invitations-btn">
            <Send className="h-3 w-3 mr-1.5" /> Send Invitations
          </Button>
          <button onClick={() => setSelected(new Set())} className="text-xs text-blue-600 hover:text-blue-800 ml-auto">Clear selection</button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#9CA3AF]" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <GraduationCap className="h-12 w-12 text-[#E5E7EB] mx-auto mb-3" />
          <p className="text-sm font-medium text-[#6B7280] mb-1">{search || gradeFilter !== 'all' || statusFilter !== 'all' ? 'No students match your filters' : 'No students yet'}</p>
          <p className="text-xs text-[#9CA3AF] mb-4">{!search && gradeFilter === 'all' ? 'Add students individually or upload a spreadsheet' : 'Try adjusting your search or filters'}</p>
          {!search && gradeFilter === 'all' && (
            <div className="flex items-center gap-2 justify-center">
              <Button variant="outline" size="sm" onClick={() => setShowUpload(true)} className="rounded-lg text-xs">
                <Upload className="h-3.5 w-3.5 mr-1.5" /> Upload Spreadsheet
              </Button>
              <Button size="sm" onClick={() => { setEditStudent(null); setShowAdd(true); }} className="rounded-lg text-xs bg-[#F97D6A] hover:bg-[#e86c5a] text-white">
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Student
              </Button>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Table */}
          <p className="text-[11px] text-[#9CA3AF] mb-2" data-testid="attendance-rule-table">
            Attendance: {ATTENDANCE_RULE}
          </p>
          <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden hidden sm:block">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="students-table">
                <thead className="bg-[#F8F9FA]">
                  <tr>
                    <th className="w-10 px-3 py-2.5">
                      <input type="checkbox" checked={selected.size === paginated.length && paginated.length > 0} onChange={toggleSelectAll} className="h-3.5 w-3.5 rounded border-[#D1D5DB]" data-testid="select-all-checkbox" />
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-[#6B7280]">Student Name</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-[#6B7280]">Grade</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-[#6B7280] hidden lg:table-cell">Division</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-[#6B7280] hidden md:table-cell">Admission No.</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-[#6B7280] hidden lg:table-cell">Parent/Guardian</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-[#6B7280] hidden xl:table-cell">Parent Email</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-[#6B7280]">Status</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-[#6B7280]">Attendance</th>
                    <th className="px-3 py-2.5 text-right text-xs font-semibold text-[#6B7280]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map(s => {
                    const sid = s.id || s._id;
                    const ob = s.onboarding_status || 'not_invited';
                    return (
                      <tr key={sid} className="border-t border-[#F3F4F6] hover:bg-[#FAFAFA] transition-colors" data-testid={`student-row-${sid}`}>
                        <td className="px-3 py-2.5">
                          <input type="checkbox" checked={selected.has(sid)} onChange={() => toggleSelect(sid)} className="h-3.5 w-3.5 rounded border-[#D1D5DB]" />
                        </td>
                        <td className="px-3 py-2.5">
                          <button onClick={() => setProfileId(sid)} className="text-left hover:text-[#F97D6A] transition-colors" data-testid={`student-name-${sid}`}>
                            <p className="font-medium text-[#1F2937]">{s.full_name}</p>
                          </button>
                        </td>
                        <td className="px-3 py-2.5 text-[#4B5563]">{s.grade}</td>
                        <td className="px-3 py-2.5 text-[#4B5563] hidden lg:table-cell">{s.division || '—'}</td>
                        <td className="px-3 py-2.5 text-[#4B5563] hidden md:table-cell font-mono text-xs">{s.admission_number || '—'}</td>
                        <td className="px-3 py-2.5 text-[#4B5563] hidden lg:table-cell">
                          {s.parent_name || '—'}
                          {!s.parent_profession && (
                            <span className="block text-[10px] text-amber-600 whitespace-nowrap" data-testid={`no-profession-${sid}`}>Profession not recorded</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-[#4B5563] hidden xl:table-cell text-xs">{s.parent_email || '—'}</td>
                        <td className="px-3 py-2.5">
                          <Badge className={`border-0 text-[10px] ${ONBOARDING_COLORS[ob]}`}>{ONBOARDING_LABELS[ob]}</Badge>
                        </td>
                        <td className="px-3 py-2.5"><AttendanceCell record={attendance[sid]} /></td>
                        <td className="px-3 py-2.5 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => setProfileId(sid)} className="p-1.5 rounded-md hover:bg-[#F3F4F6] text-[#6B7280] hover:text-[#1F2937] transition-colors" title="View" data-testid={`view-student-${sid}`}>
                              <Eye className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => { setEditStudent(s); setShowAdd(true); }} className="p-1.5 rounded-md hover:bg-[#F3F4F6] text-[#6B7280] hover:text-[#1F2937] transition-colors" title="Edit" data-testid={`edit-student-${sid}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            {ob === 'not_invited' && s.parent_email && (
                              <button onClick={() => handleResend(sid)} disabled={resending === sid} className="p-1.5 rounded-md hover:bg-[#FFF0ED] text-[#9CA3AF] hover:text-[#F97D6A] transition-colors" title="Send Invitation" data-testid={`invite-student-${sid}`}>
                                {resending === sid ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                              </button>
                            )}
                            {(ob === 'invited') && (
                              <button onClick={() => handleResend(sid)} disabled={resending === sid} className="p-1.5 rounded-md hover:bg-[#FFF0ED] text-[#9CA3AF] hover:text-[#F97D6A] transition-colors" title="Resend Invitation" data-testid={`resend-student-${sid}`}>
                                {resending === sid ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Phone view: the same students as cards, so nothing is cut off the right edge */}
          <div className="sm:hidden space-y-2" data-testid="students-card-list">
            {paginated.length > 0 && (
              <label className="flex items-center gap-2 px-1 py-2 text-xs font-semibold text-[#6B7280]">
                <input type="checkbox" checked={selected.size === paginated.length && paginated.length > 0} onChange={toggleSelectAll}
                       className="h-4 w-4 rounded border-[#D1D5DB]" data-testid="card-select-all-checkbox" />
                Select all on this page
              </label>
            )}
            {paginated.map(s => {
              const sid = s.id || s._id;
              const ob = s.onboarding_status || 'not_invited';
              return (
                <div key={sid} className="bg-white rounded-xl border border-[#E5E7EB] p-3" data-testid={`student-card-${sid}`}>
                  <div className="flex items-start gap-3">
                    <input type="checkbox" checked={selected.has(sid)} onChange={() => toggleSelect(sid)}
                           className="h-4 w-4 mt-1 rounded border-[#D1D5DB] shrink-0" aria-label={`Select ${s.full_name}`} />
                    <button onClick={() => setProfileId(sid)} className="flex-1 min-w-0 text-left" data-testid={`student-card-name-${sid}`}>
                      <p className="font-semibold text-[#1F2937] break-words">{s.full_name}</p>
                      <p className="text-xs text-[#6B7280] mt-0.5">
                        Grade {s.grade}{s.division ? `-${s.division}` : ''} · {s.admission_number || 'No admission no.'}
                      </p>
                      <p className="text-xs text-[#9CA3AF] mt-0.5 break-words">{s.parent_name || 'Parent not recorded'}</p>
                      {!s.parent_profession && (
                        <span className="block text-[10px] text-amber-600 mt-0.5">Profession not recorded</span>
                      )}
                    </button>
                  </div>
                  <div className="flex items-center gap-2 mt-2.5 flex-wrap">
                    <Badge className={`border-0 text-[10px] ${ONBOARDING_COLORS[ob]}`}>{ONBOARDING_LABELS[ob]}</Badge>
                    <AttendanceCell record={attendance[sid]} />
                  </div>
                  <div className="flex items-center gap-2 mt-2.5 border-t border-[#F3F4F6] pt-2.5">
                    <button onClick={() => setProfileId(sid)} className="flex-1 h-11 rounded-lg text-xs font-semibold text-[#4B5563] bg-[#F8F9FA] active:bg-[#F3F4F6]" data-testid={`card-view-student-${sid}`}>
                      View
                    </button>
                    <button onClick={() => { setEditStudent(s); setShowAdd(true); }} className="flex-1 h-11 rounded-lg text-xs font-semibold text-[#4B5563] bg-[#F8F9FA] active:bg-[#F3F4F6]" data-testid={`card-edit-student-${sid}`}>
                      Edit
                    </button>
                    {(ob === 'invited' || (ob === 'not_invited' && s.parent_email)) && (
                      <button onClick={() => handleResend(sid)} disabled={resending === sid}
                              className="flex-1 h-11 rounded-lg text-xs font-semibold text-[#F97D6A] bg-[#FFF0ED] active:bg-[#ffe3dd] disabled:opacity-50"
                              data-testid={`card-invite-student-${sid}`}>
                        {resending === sid ? 'Sending…' : ob === 'invited' ? 'Resend' : 'Invite'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 px-1">
              <p className="text-xs text-[#9CA3AF]">
                Page {page} of {totalPages} · Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
              </p>
              <div className="flex items-center gap-1">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="h-11 w-11 sm:h-auto sm:w-auto sm:p-1.5 flex items-center justify-center rounded-md hover:bg-[#F3F4F6] disabled:opacity-30 text-[#6B7280]" data-testid="prev-page">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let p;
                  if (totalPages <= 5) p = i + 1;
                  else if (page <= 3) p = i + 1;
                  else if (page >= totalPages - 2) p = totalPages - 4 + i;
                  else p = page - 2 + i;
                  return (
                    <button key={p} onClick={() => setPage(p)}
                      className={`w-11 h-11 sm:w-7 sm:h-7 rounded-md text-xs font-medium ${p === page ? 'bg-[#F97D6A] text-white' : 'text-[#6B7280] hover:bg-[#F3F4F6]'}`}>
                      {p}
                    </button>
                  );
                })}
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="h-11 w-11 sm:h-auto sm:w-auto sm:p-1.5 flex items-center justify-center rounded-md hover:bg-[#F3F4F6] disabled:opacity-30 text-[#6B7280]" data-testid="next-page">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Dialogs */}
      <StudentFormDialog open={showAdd} onOpenChange={setShowAdd} student={editStudent} onSaved={fetchStudents} />
      <UploadDialog open={showUpload} onOpenChange={setShowUpload} onImported={fetchStudents} />
      <InvitationDialog open={showInvite} onOpenChange={setShowInvite} selectedIds={eligibleForInvite} onSent={() => { setSelected(new Set()); fetchStudents(); }} />
    </div>
  );
}
